import { Button } from '@/lib/Buttons';
import { Audience, AudienceSyncTimeBasedSchedule, Expression } from '@/models/audiences';
import { RootState, useAppDispatch } from '@/reducers';
import { useSaveAudienceMutation, useUpdateAudienceMutation, useUpdateAudienceScheduleMutation } from '@/services/endpoints/audiences';
import { useGetTraitsQuery } from '@/services/endpoints/traits';
import { FormikProps } from 'formik';
import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AudienceEditSource, setAudienceEditSource, setExpressionReadOnly } from '../audienceEdit/audienceEditSlice';
import { useWorkspace } from '../workspaces/hooks';
import { clearStashedAudience, stashAudience } from './audienceEditSlice';
import { AudienceContent, AudienceFormModel } from './components';
import { AudienceSyncFormModel } from './components/sync/AudienceSyncContainer';
import { AUDIENCE_EDIT_STEPS } from './constants';
import { IModalContentProps } from '@/models/modals';
import { AudienceModalContent } from './components/AudienceModalContent';
import { useCreateStepper } from '@/lib/Steppers';

type AudienceEditProps = {
  onSuccess: (audience: Audience) => void;
  initialAudience?: Partial<Audience>;
  source?: AudienceEditSource;
  /** Fired after step-1 create succeeds (server record exists; expression may not be persisted yet). */
  onAudienceCreated?: (audienceId: string) => void;
  /** Fired after the expression step PATCH succeeds. */
  onExpressionPersisted?: () => void;
} & IModalContentProps;

const hasMeaningfulExpression = (expression?: Record<string, Expression> | null) => Boolean(expression && Object.keys(expression).length > 0);

export const AudienceEditV2: FC<AudienceEditProps> = ({ onSuccess, closeModal, initialAudience, source, onAudienceCreated, onExpressionPersisted }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('audience_edit');

  const createFormRef = useRef<FormikProps<AudienceFormModel>>(null);
  const syncFormRef = useRef<FormikProps<AudienceSyncFormModel>>(null);

  const { steps, currentStepId, currentStepIndex, goToNextStep, isLastStep, completeCurrentStep } = useCreateStepper(AUDIENCE_EDIT_STEPS);
  const workspace = useWorkspace();

  const stashedAudience = useSelector((state: RootState) => state.audienceEdit.audience);
  const editSource = useSelector((state: RootState) => state.audienceEdit.source);
  const isPrefillReadOnly = (source ?? editSource) === 'prefill';
  const stashedExpressionRef = useRef(stashedAudience?.expression);
  stashedExpressionRef.current = stashedAudience?.expression;

  const audienceForForm = currentStepId === 'nameAndMetadata' ? (initialAudience ?? undefined) : stashedAudience;

  const { data: traits } = useGetTraitsQuery({
    workspaceId: workspace.id,
  });

  const [saveAudience, { isLoading: isSaveLoading, data: createdAudience, isSuccess: isSaveSuccess }] = useSaveAudienceMutation();
  const [updateAudience, { isLoading: isUpdateLoading, data: updatedAudience, isSuccess: isUpdateSuccess }] = useUpdateAudienceMutation();
  const [updateAudienceSchedule, { isLoading: isUpdateScheduleLoading, data: updatedAudienceSchedule, isSuccess: isUpdateScheduleSuccess }] =
    useUpdateAudienceScheduleMutation();

  const isLoading = isSaveLoading || isUpdateLoading || isUpdateScheduleLoading;

  const isModal = !!closeModal;

  useEffect(() => {
    if (initialAudience) {
      dispatch(clearStashedAudience());
      dispatch(stashAudience(initialAudience));
      if (source === 'prefill') {
        dispatch(setAudienceEditSource('prefill'));
        dispatch(setExpressionReadOnly(hasMeaningfulExpression(initialAudience.expression)));
      } else {
        dispatch(setExpressionReadOnly(false));
      }
      return;
    }

    dispatch(clearStashedAudience());
  }, [dispatch, initialAudience, source]);

  useEffect(() => {
    return () => {
      dispatch(setAudienceEditSource(undefined));
      dispatch(setExpressionReadOnly(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!createdAudience) {
      return;
    }

    if (createdAudience.id) {
      onAudienceCreated?.(createdAudience.id);
    }

    const { expression, ...rest } = createdAudience;
    dispatch(
      stashAudience({
        ...rest,
        ...(hasMeaningfulExpression(expression)
          ? { expression }
          : hasMeaningfulExpression(stashedExpressionRef.current)
            ? { expression: stashedExpressionRef.current }
            : {}),
      })
    );
  }, [createdAudience, dispatch, onAudienceCreated]);

  useEffect(() => {
    if (!updatedAudience) {
      return;
    }

    onExpressionPersisted?.();

    const { expression, ...rest } = updatedAudience;
    dispatch(
      stashAudience({
        ...rest,
        ...(hasMeaningfulExpression(expression)
          ? { expression }
          : hasMeaningfulExpression(stashedExpressionRef.current)
            ? { expression: stashedExpressionRef.current }
            : {}),
      })
    );
  }, [updatedAudience, dispatch, onExpressionPersisted]);

  useEffect(() => {
    if (updatedAudienceSchedule) {
      dispatch(stashAudience(updatedAudienceSchedule));
    }
  }, [updatedAudienceSchedule, dispatch]);

  const createAudience = (audience: Partial<Audience>) => {
    if (audience.datasourceIds && audience.matchingTrait && audience.name) {
      saveAudience({
        workspaceId: workspace.id,
        audience: {
          name: audience.name,
          matchingTrait: audience.matchingTrait,
          datasourceIds: audience.datasourceIds,
        },
      });
    }
  };

  const handleSubmit = async (form: AudienceFormModel) => {
    if (form.name && form.matchingTrait && form.datasourceIds) {
      // Do not replace a stashed prefill expression with form values that omit it.
      dispatch(
        stashAudience({
          name: form.name,
          matchingTrait: form.matchingTrait,
          datasourceIds: form.datasourceIds,
        })
      );
    }
  };

  const updateSchedule = (audienceId: string, syncForm: AudienceSyncFormModel) => {
    const syncData: AudienceSyncFormModel = { ...syncForm };
    let scheduleConfiguration: AudienceSyncTimeBasedSchedule;

    switch (syncForm.scheduleType) {
      case 'REALTIME':
        syncData.scheduleConfiguration = {};
        break;

      case 'MANUAL':
        syncData.scheduleConfiguration = {
          emailNotification: syncForm.scheduleConfiguration.emailNotification || false,
          createSnapshotOnRefresh: syncForm.scheduleConfiguration.createSnapshotOnRefresh || false,
        };
        break;

      case 'SCHEDULED':
        scheduleConfiguration = {
          ...(syncForm.scheduleConfiguration as AudienceSyncTimeBasedSchedule),
          emailNotification: syncForm.scheduleConfiguration.emailNotification || false,
          createSnapshotOnRefresh: syncForm.scheduleConfiguration.createSnapshotOnRefresh || false,
        };

        switch (scheduleConfiguration.unit) {
          case 'HOURS':
          case 'DAYS':
            delete scheduleConfiguration.weekDays;
            delete scheduleConfiguration.dayOfMonth;
            break;

          case 'WEEKS':
            delete scheduleConfiguration.dayOfMonth;
            break;

          case 'MONTHS':
            delete scheduleConfiguration.weekDays;
            break;
        }

        syncData.scheduleConfiguration = scheduleConfiguration;
        break;
    }

    updateAudienceSchedule({
      workspaceId: workspace.id,
      audienceId,
      audience: syncData,
    });
  };

  const completeAndGoNextStep = () => {
    completeCurrentStep();
    goToNextStep();
  };

  const handleNextButtonClicked = async () => {
    if (currentStepId === 'nameAndMetadata') {
      await createFormRef.current?.submitForm();
      const audienceForm = createFormRef.current?.values;
      if (createFormRef.current?.isValid && audienceForm) {
        createAudience(audienceForm);
      }
      return;
    }

    if (currentStepId === 'expression') {
      if (stashedAudience?.id && stashedAudience?.name && stashedAudience?.expression) {
        updateAudience({
          workspaceId: workspace.id,
          audienceId: stashedAudience?.id,
          audience: {
            name: stashedAudience.name,
            expression: stashedAudience.expression,
          },
        });
      }
      return;
    }

    if (currentStepId === 'sync') {
      await syncFormRef.current?.submitForm();
      const syncForm = syncFormRef.current?.values;

      if (stashedAudience?.id && syncFormRef.current?.isValid && syncForm) {
        updateSchedule(stashedAudience?.id, syncForm);
      }
      return;
    }

    if (currentStepId === 'confirmation') {
      // closeModal();
      return;
    }

    completeAndGoNextStep();
  };

  useEffect(() => {
    if (isSaveSuccess) {
      completeAndGoNextStep();
    }
  }, [isSaveSuccess]);

  useEffect(() => {
    if (isUpdateSuccess) {
      completeAndGoNextStep();
    }
  }, [isUpdateSuccess]);

  useEffect(() => {
    if (isUpdateScheduleSuccess) {
      completeAndGoNextStep();
    }
  }, [isUpdateScheduleSuccess]);

  const mergedTraits = traits ? [...traits.customer, ...traits.event] : undefined;

  const isStep2WithNoExpressions = currentStepId === 'expression' && (!stashedAudience?.expression || Object.keys(stashedAudience.expression).length === 0);

  const nextButton = (
    <div className='flex shrink-0 justify-end border-t border-gray-200 px-6 py-4'>
      <Button
        type='submit'
        className={isLastStep && 'bg-green-500'}
        onClick={handleNextButtonClicked}
        disabled={isStep2WithNoExpressions}
        isLoading={isLoading}
        text={t(!isLastStep ? 'next' : 'finish')}
      />
    </div>
  );


  return (
    <>
      {isModal ? (
        <AudienceModalContent
          createFormRef={createFormRef}
          onCreateFormSubmit={handleSubmit}
          steps={steps}
          stepId={currentStepId}
          currentStepIndex={currentStepIndex}
          syncFormRef={syncFormRef}
          traits={mergedTraits}
          audience={audienceForForm}
          onSuccess={onSuccess}
          closeModal={closeModal}
          readOnly={isPrefillReadOnly}
        >
          {!isLastStep && nextButton}
        </AudienceModalContent>
      ) : (
        <AudienceContent
          createFormRef={createFormRef}
          onCreateFormSubmit={handleSubmit}
          steps={steps}
          stepId={currentStepId}
          currentStepIndex={currentStepIndex}
          syncFormRef={syncFormRef}
          traits={mergedTraits}
          audience={audienceForForm}
          readOnly={isPrefillReadOnly}
        >
          {!isLastStep && nextButton}
        </AudienceContent>
      )}
    </>
  );
};
